import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {getVariablePairLabelFromDomainLabels, isVariableDomain} from 'antibodies/AntibodyUtils';
import {
  type ComponentFormatKey,
  type ComponentInputValue,
  type ProteinCreationCellValue,
  ProteinCreationCellValueType,
  type ProteinCreationInputRow,
  ProteinCreationRow,
  type ProteinCreationTableRequest,
  type SchemaFieldInputValue,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {generateSpreadsheetTemplateHeaders} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  NdcGermlineConfig,
  type NdcGermlineConfigRequest,
  NdcRelatedSchemasConfig,
  NdcSpreadsheetComponentFormatType,
  TranslationParameters,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {AntibodyDomainType, RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertNever} from 'util/Types';

interface PairInfo {
  vhVaVgLabel: string;
  vlVbVdLabel: string;
}

function getGermlineIdForDomain(
  germlineConfig: NdcGermlineConfig,
  domainType: AntibodyDomainType
): string | null {
  if (
    domainType === AntibodyDomainType.L ||
    domainType === AntibodyDomainType.F ||
    domainType === AntibodyDomainType.ELEMENT
  ) {
    return null;
  }
  if (!germlineConfig.useMultipleGermlines) {
    return germlineConfig.selectedGermlineId;
  }
  if (isVariableDomain(domainType)) {
    return germlineConfig.selectedVariableGermlineId;
  }
  return germlineConfig.selectedConstantGermlineId;
}

function buildGermlineConfigs(
  germlineConfig: NdcGermlineConfig,
  format: AntibodyFormat
): Array<NdcGermlineConfigRequest> {
  const configs: Array<NdcGermlineConfigRequest> = [];
  for (const domain of format.domains) {
    const germlineId = getGermlineIdForDomain(germlineConfig, domain.type);
    if (germlineId != null) {
      configs.push({domainKey: {label: domain.label}, germlineId});
    }
  }
  return configs;
}

function toSchemaFieldInputValue(value: ProteinCreationCellValue): SchemaFieldInputValue | null {
  switch (value.type) {
    case ProteinCreationCellValueType.RAW_TEXT:
    case ProteinCreationCellValueType.NUMBER:
      return {type: 'RAW_TEXT', text: value.text};
    case ProteinCreationCellValueType.BOOLEAN:
      return {type: 'BOOLEAN', value: value.value};
    case ProteinCreationCellValueType.ENTITY_LINK:
      return {type: 'EXISTING_ENTITIES', entityIds: value.entities.map(({id}) => id)};
    case ProteinCreationCellValueType.DROPDOWN_OPTION:
      return {type: 'DROPDOWN_OPTION', optionId: value.optionId};
    case ProteinCreationCellValueType.EMPTY:
    case ProteinCreationCellValueType.EXISTING_ENTITY:
      return null;
    default:
      assertNever(value);
      return null;
  }
}

function buildSchemaFieldInputs(
  schemaFields: Record<string, ProteinCreationCellValue>
): Record<string, SchemaFieldInputValue> {
  const inputs: Record<string, SchemaFieldInputValue> = {};
  for (const [fieldApiId, value] of Object.entries(schemaFields)) {
    const input = toSchemaFieldInputValue(value);
    if (input != null) {
      inputs[fieldApiId] = input;
    }
  }
  return inputs;
}

export interface BuildProteinCreationTableRequestInput {
  rows: ReadonlyArray<ProteinCreationRow>;
  format: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
  proteinSchemaId: string;
  folderId: string;
  relatedSchemasConfig: NdcRelatedSchemasConfig;
  translationParameters: TranslationParameters;
  germlineConfig: NdcGermlineConfig;
  documentId: string | null;
  namingStrategy: RegistryLabelingStrategy | null;
}

export function buildProteinCreationTableRequest(
  input: BuildProteinCreationTableRequestInput
): ProteinCreationTableRequest {
  const {
    rows,
    format,
    startingType,
    proteinSchemaId,
    folderId,
    relatedSchemasConfig,
    translationParameters,
    germlineConfig,
    documentId,
    namingStrategy,
  } = input;

  const columnNames = generateSpreadsheetTemplateHeaders(format, startingType);

  const pairLabelToPair = new Map<string, PairInfo>();
  if (startingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS) {
    for (const pair of format.distinctVariablePairs) {
      const label = getVariablePairLabelFromDomainLabels(pair.vhVaVg.label, pair.vlVbVd.label);
      pairLabelToPair.set(label, {vhVaVgLabel: pair.vhVaVg.label, vlVbVdLabel: pair.vlVbVd.label});
    }
  }

  const requestRows: Array<ProteinCreationInputRow> = [];
  for (const row of rows) {
    const nestedComponents: Array<{componentFormatKey: ComponentFormatKey; value: ComponentInputValue}> = [];
    for (const colName of columnNames) {
      const cellValue = row.nestedComponents[colName];
      let value: ComponentInputValue;
      if (cellValue?.type === ProteinCreationCellValueType.RAW_TEXT) {
        value = {type: 'RAW_TEXT', text: cellValue.text};
      } else if (cellValue?.type === ProteinCreationCellValueType.EXISTING_ENTITY) {
        value = {type: 'EXISTING_ENTITY', entityId: cellValue.entityId};
      } else {
        continue;
      }

      let componentFormatKey: ComponentFormatKey;
      if (startingType === NdcSpreadsheetComponentFormatType.CHAINS) {
        componentFormatKey = {keyType: 'CHAIN', label: colName};
      } else if (startingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS) {
        const pair = pairLabelToPair.get(colName);
        if (pair != null) {
          componentFormatKey = {
            keyType: 'VARIABLE_PAIR',
            vhVaVg: {label: pair.vhVaVgLabel},
            vlVbVd: {label: pair.vlVbVdLabel},
          };
        } else {
          componentFormatKey = {keyType: 'DOMAIN', label: colName};
        }
      } else {
        componentFormatKey = {keyType: 'DOMAIN', label: colName};
      }

      nestedComponents.push({componentFormatKey, value});
    }
    const name = row.name.trim();
    const alias = row.alias.trim();
    requestRows.push({
      nestedComponents,
      schemaFields: buildSchemaFieldInputs(row.schemaFields),
      ...(name !== '' ? {name} : {}),
      ...(alias !== '' ? {alias} : {}),
    });
  }

  return {
    folderId,
    proteinSchemaId,
    componentFormatType: startingType,
    rows: requestRows,
    newComponentConfig: {
      aaPairSchemaId: relatedSchemasConfig.aaPairSchemaId,
      dnaDomainSchemaId: relatedSchemasConfig.dnaDomainSchemaId,
      dnaChainSchemaId: relatedSchemasConfig.dnaChainSchemaId,
      dnaPairSchemaId: relatedSchemasConfig.dnaPairSchemaId,
    },
    translationParameters: {geneticCode: translationParameters.geneticCode},
    germlineConfigs: buildGermlineConfigs(germlineConfig, format),
    entryId: documentId,
    namingStrategy,
  };
}
